import { Link } from "react-router-dom";
import { Linkedin, Twitter, ArrowUpRight } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";
import { toast } from "sonner";
import api from "@/lib/api";
import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Look out for our AI briefings.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-border mt-10" data-testid="site-footer">
      <div className="aurora opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-6">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src={COMPANY.logo} alt="CloudLab" className="h-9 w-auto" />
              <span className="font-heading font-bold text-xl">CloudLab Technologies</span>
            </Link>
            <p className="text-muted-foreground max-w-md leading-relaxed">
              We architect intelligent AI systems and enterprise software for the world&apos;s most ambitious companies.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@company.com"
                className="flex-1 bg-transparent border border-border rounded-full px-5 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                data-testid="newsletter-email-input"
              />
              <button
                disabled={loading}
                type="submit"
                className="rounded-full px-5 bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {loading ? "…" : "Subscribe"}
              </button>
            </form>
            <div className="mt-8 flex gap-3">
              <a href={COMPANY.socials.linkedin} target="_blank" rel="noreferrer" className="p-2 rounded-full glass hover:text-primary" aria-label="LinkedIn"><Linkedin className="w-4 h-4" /></a>
              <a href={COMPANY.socials.twitter} target="_blank" rel="noreferrer" className="p-2 rounded-full glass hover:text-primary" aria-label="Twitter"><Twitter className="w-4 h-4" /></a>
            </div>

            {/* Bold outlined watermark filling the leftover whitespace under the subscribe form */}
            <div className="mt-10 select-none pointer-events-none" data-testid="footer-since">
              <div
                className="font-heading font-black italic leading-[0.85] tracking-tight text-transparent whitespace-nowrap"
                style={{
                  WebkitTextStroke: "1.5px hsl(var(--foreground) / 0.18)",
                  fontSize: "clamp(3.5rem, 9vw, 6.5rem)",
                }}
                aria-label="Since 2012"
              >
                since 2012
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">Services</div>
            <ul className="space-y-2">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-foreground/80 hover:text-primary">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">Industries</div>
            <ul className="space-y-2">
              <li>
                <Link to="/industries/education" className="text-sm text-foreground/80 hover:text-primary" data-testid="footer-industry-education">Education</Link>
              </li>
              <li>
                <Link to="/industries" className="text-sm text-foreground/80 hover:text-primary" data-testid="footer-industries-hub">All industries</Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">Company</div>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm hover:text-primary">About</Link></li>
              <li><Link to="/blog" className="text-sm hover:text-primary">Insights</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-primary">Contact</Link></li>
              <li><Link to="/quote" className="text-sm hover:text-primary inline-flex items-center gap-1">Request quote <ArrowUpRight className="w-3 h-3" /></Link></li>
            </ul>
            <div className="mt-6 text-xs text-muted-foreground space-y-1">
              <div>{COMPANY.address}</div>
              <a href={`mailto:${COMPANY.email}`} className="block hover:text-primary">{COMPANY.email}</a>
              <div>{COMPANY.phone}</div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} CloudLab Technologies. Built for the AI-first enterprise.</div>
          <div className="flex items-center gap-4">
            <div className="font-mono uppercase tracking-[0.2em]">Serving {COMPANY.markets.join(" · ")}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
